import { $inject, $type } from "../utils/di-container.js";

const tryJsonParse = str => {
  try {
    return JSON.parse(str);
  } catch (e) {
    // ignore
  }
}

const padOrCutTo = (len, str) => {
  if (str.length > len) {
    return str.slice(-len);
  } else if (str.length < len) {
    return str.padStart(len, '0');
  }
  return str;
}

const qs = (...queries) => {
  for (let i=0; i<queries.length; i++) {
    const el = document.querySelector(queries[i]);
    if (el) {
      return el;
    }
  }
}

const eqs = (element, ...queries) => {
  for (let i=0; i<queries.length; i++) {
    const el = element.querySelector(queries[i]);
    if (el) {
      return el;
    }
  }
}

/**
 * Service used to scrape relevant data from the target retailer page and make it available, e.g. to send it via badge requests towards the authby backend.
 */
export class ScrapingService {

  constructor() {
  }

  [$inject](obtain) {
  }

  scrapData() {
    let priceAmount, priceCurrency, productId, productName, brandName;

    // 1. load data from ogp def
    const ogTypeMeta = qs('meta[property="og:type"]');
    if (ogTypeMeta && ogTypeMeta.getAttribute('content') === 'product') {
      const ogPriceAmountMetaEl = qs('meta[property="og:price:amount"]', 'meta[property="product:price:amount"]', 'meta[property="og:product:price:amount"]');
      if (ogPriceAmountMetaEl) {
        priceAmount = Number.parseFloat(ogPriceAmountMetaEl.getAttribute('content'));
      }
      const ogPriceCurrencyMetaEl = qs('meta[property="og:price:currency"]', 'meta[property="product:price:currency"]', 'meta[property="og:product:price:currency"]');
      if (ogPriceCurrencyMetaEl) {
        priceCurrency = ogPriceCurrencyMetaEl.getAttribute('content')?.toUpperCase().trim();
      }
      const ogProductIdEl = qs('meta[property="og:gtin13"]', 'meta[property="product:gtin13"]', 'meta[property="og:product:gtin13"]', 'meta[property="og:gtin8"]', 'meta[property="product:gtin8"]', 'meta[property="og:product:gtin8"]', 'meta[property="og:gtin14"]', 'meta[property="product:gtin14"]', 'meta[property="og:product:gtin14"]', 'meta[property="og:gtin12"]', 'meta[property="product:gtin12"]', 'meta[property="og:product:gtin12"]', 'meta[property="og:gtin"]', 'meta[property="product:gtin"]', 'meta[property="og:product:gtin"]');
      if (ogProductIdEl) {
        productId = ogProductIdEl.getAttribute('content');
      }
      const ogProductNameEl = qs('meta[property="og:name"]', 'meta[property="product:name"]', 'meta[property="og:product:name"]');
      if (ogProductNameEl) {
        productName = ogProductNameEl.getAttribute('content');
      }
      const ogBrandNameEl = qs('meta[property="og:brand:name"]', 'meta[property="product:brand:name"]', 'meta[property="product:brand"]', 'meta[property="og:product:brand:name"]');
      if (ogBrandNameEl) {
        brandName = ogBrandNameEl.getAttribute('content');
      }
    }

    // 2. load data from schema.org json
    const extractFromJson = (productData) => {
      if (productData) {
        priceAmount = priceAmount || Number.parseFloat(productData.offers?.price);
        priceCurrency = priceCurrency || productData.offers?.priceCurrency?.toUpperCase().trim();
        productId = productId || productData.gtin13 || productData.gtin8 || productData.gtin14 || productData.gtin12 || productData.gtin || null;
        productName = productName || productData.name;
        brandName = brandName || productData.brand?.name || productData.manufacturer?.name;
      }
    }

    const jsonScripts = document.querySelectorAll('script[type="application/ld+json"]');

    let productData = Array.from(jsonScripts)
      .map(scr => tryJsonParse(scr.innerText))
      .filter(_ => !!_)
      .flatMap(json => Array.isArray(json) ? json : [json])
      .filter(obj => obj?.['@context']?.includes('schema.org'))
      .map(obj => obj['@graph'] ? obj['@graph'] : obj)
    if (productData) {
      productData = Array.isArray(productData) ? productData : [productData];
    }
    if (productData) {
      productData = productData.find(obj => obj?.['@type'] === 'Product')
        || productData.find(obj => obj?.['@type'] === 'ProductGroup');
    }

    extractFromJson(productData);
    if (productData?.hasVariant) {
      extractFromJson(Array.isArray(productData.hasVariant) ? productData.hasVariant?.[0] : productData.hasVariant);
    }

    if (productData) {
      // augment model data as well
      let modelData = productData.model;
      if (modelData?.length) {
        modelData = modelData[0];
      }
      extractFromJson(modelData);
    }

    // 3. load data from schema.org tags
    const extractFromTag = productDataEl => {
      if (productDataEl) {
        const priceEl = eqs(productDataEl, '*[itemprop="offers"] *[itemprop="price"]');
        if (priceEl) {
          const priceContent = priceEl.getAttribute('content');
          if (priceContent) {
            priceAmount = priceAmount || Number.parseFloat(priceContent);
          }
        }
        const currencyEl = eqs(productDataEl, '*[itemprop="offers"] *[itemprop="priceCurrency"]');
        if (currencyEl) {
          priceCurrency = priceCurrency || (currencyEl.getAttribute('content') || currencyEl.innerText)?.toUpperCase()?.trim();
        }
        const productIdEl = eqs(productDataEl, '*[itemprop="gtin13"]', '*[itemprop="gtin8"]', '*[itemprop="gtin14"]', '*[itemprop="gtin12"]', '*[itemprop="gtin"]');
        if (productIdEl) {
          productId = productId || productIdEl.getAttribute('content') || productIdEl.innerText;
        }
        const productNameEl = eqs(productDataEl, '*[itemprop="name"]');
        if (productNameEl) {
          productName = productName || productNameEl.getAttribute('content') || productNameEl.innerText;
        }
        const brandNameEl = eqs(productDataEl, '*[itemprop="brand"] *[itemprop="name"]', '*[itemprop="brand"]');
        if (brandNameEl) {
          brandName = brandName || brandNameEl.getAttribute('content') || brandNameEl.innerText;
        }
      }
    }
    const productEl = qs('*[itemscope][itemtype$="schema.org/Product"]');
    extractFromTag(productEl);

    // 4 load data from analytics tags
    try {
      if (window.dataLayer) {
        for (let i = 0; i < window.dataLayer.length; i++) {
          let data = window.dataLayer[i];
          if (data.ecommerce && data.ecommerce.detail && data.ecommerce.detail.products) {
            let product = data.ecommerce.detail.products[0];
            productId = productId || product.id || product.ean || null;
            brandName = brandName || product.brand || null;
          }
        }
      }
      if (window.ga) {
        ga(function () {
          let trackers = ga.getAll();
          trackers.forEach(tracker => {
            let pageData = tracker.get('page');
            if (pageData.includes('ecommerce')) {
              productId = productId || tracker.get('ecommerce.detail.products.0.id') || null;
              brandName = brandName || tracker.get('ecommerce.detail.products.0.brand') || null;
            }
          });
        });
      }
      if (window.gtag) {
        let events = window.gtag.q || [];
        for (let event of events) {
          if (event[0] === 'event' && event[1] === 'view_item') {
            let params = event[2];
            if (params && params.items && params.items.length > 0) {
              let product = params.items[0];
              productId = productId || product.item_id || null;
              brandName = brandName || product.brand || null;
            }
          }
        }
      }
    } catch (e) {
      // ga specific code / catch just to be safe ignore any possible errors
    }

    if (productId) {
      productId = padOrCutTo(14, productId);
    }

    let data;
    if (priceAmount || productId || productName || brandName) {
      data =  {
        priceAmount,
        priceCurrency,
        productId,
        productName,
        brandName,
      }
    }

    return data;
  }

}

ScrapingService[$type] = Symbol('ScrapingService');
